import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Injector,
  Input,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { PrimeNGConfig } from "primeng/api";
import { Calendar } from "primeng/calendar";

import { FieldCoreComponent } from "../FieldCoreComponent";
import { PrimengTranslateAdapter } from "./PrimengTranslateAdapter";
import { MedDatepickerModeType, MedDatepickerValueType } from "../types";
import { STRING } from "../../../constants/string";

type ValidatorFn = (v: any) => boolean;

export enum MODE_TYPES {
  SINGLE = "single",
  RANGE = "range",
  MULTIPLE = "multiple",
}

const defaultValueAdapter = new Map<MedDatepickerModeType, any>()
  .set(MODE_TYPES.SINGLE, "")
  .set(MODE_TYPES.RANGE, null)
  .set(MODE_TYPES.MULTIPLE, []);

const valueExistAdapter = new Map<MedDatepickerModeType, ValidatorFn>()
  .set(MODE_TYPES.SINGLE, value => Boolean(value))
  .set(MODE_TYPES.RANGE, value => Boolean(value && value.length))
  .set(MODE_TYPES.MULTIPLE, value => Boolean(value && value.length));

/**
 * https://www.primefaces.org/primeng/calendar
 */
@Component({
  selector: "med-datepicker",
  templateUrl: "./med-datepicker.component.html",
  styleUrls: ["./med-datepicker.component.scss"],
  providers: [{
    multi: true,
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MedDatepickerComponent),
  }],
})
export class MedDatepickerComponent extends FieldCoreComponent<MedDatepickerValueType> {
  constructor(
    private primeConfig: PrimeNGConfig,
    injector: Injector,
    cd: ChangeDetectorRef,
  ) {
    super(injector, cd);
  }

  @ViewChild("fieldRef") fieldRef!: Calendar;
  @Input() mode: MedDatepickerModeType = "single";
  @Input() pattern = "dd.mm.yy";
  @Input() showTime = false;
  @Input() numberOfMonths = 1;

  /*
    When dialog includes other components with overlays such as dropdown,
    the overlay part cannot exceed dialog boundaries due to overflow.
    In order to solve this, you can either append the overlay to the body by using appendTo property or allow overflow in dialog.

    Possible value: body
  */
  @Input() appendTo?: HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined | any;

  get hasValue(): boolean {
    const validator = valueExistAdapter.get(this.mode);
    return !!validator && validator(this.value);
  }

  get panelStyleClass(): string {
    return this.showTime ? "flexible" : "";
  }

  @Input() set translate(value: Record<string, string>) {
    this.primeConfig.setTranslation(PrimengTranslateAdapter.format(value));
  }

  clear(): void {
    this.value = defaultValueAdapter.get(this.mode) || "";
    this.onInput.emit(this.value);
  }

  onSelect() {
    if (this.mode !== "range") {
      return;
    }

    const [, toValue] = Array.isArray(this.value) ? this.value : (this.value as string).split(STRING.COMMA);

    if (toValue) {
      this.onInput.emit(this.value);
      this.fieldRef.hideOverlay();
    }
  }
}
